import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
	CardAuthorMark,
	Navigation,
	PremadeQuizCardComponent,
} from '@quizzach/components';
import { usePremadeQuizzes } from '@quizzach/api-hooks';
import { useWindowListener } from '@quizzach/contexts';
import { IPremadeQuiz, RoutePath } from '@quizzach/interfaces';
import {
	Reveal,
	KINETIC_REPEATS,
	useKineticLine,
	useMarqueeDrag,
	useParallax,
} from '@quizzach/motion';
import { IcArrowRight, IcTv } from '@quizzach/assets';
import { scrollToTop } from '@quizzach/utils';
import {
	ArrowLink,
	breakpoints,
	BtnInk,
	BtnTeal,
	Covers,
	Finale,
	Formats,
	FRow,
	GiftPromo,
	GiftSection,
	Hero,
	HeroMedia,
	HomePage,
	Kinetic,
	Marquee,
	Paper,
	PaperCta,
	RollCard,
	SaleSlot,
	SecHead,
	Steps,
	TvBlock,
} from '@quizzach/styles';

import { HeroVoucher } from './voucher-shop/GiftCards';

/** "6.00" -> "6 EUR", "7.50" -> "7,50 EUR" */
const formatPrice = (price?: string) => {
	const value = parseFloat(price ?? '0');
	if (Number.isNaN(value)) return '';
	const text = Number.isInteger(value)
		? String(value)
		: value.toFixed(2).replace('.', ',');
	return `${text} EUR`;
};

const KineticLine = ({
	words,
	factor,
	className,
}: {
	words: string[];
	factor: number;
	className: string;
}) => {
	const ref = useRef<HTMLDivElement>(null);
	useKineticLine(ref, factor);
	const repeated = Array.from({ length: KINETIC_REPEATS }, () => words).flat();

	return (
		<div ref={ref} className={`k-line ${className}`}>
			{repeated.map((word, i) => (
				<React.Fragment key={i}>
					{word}
					<span className='dot'>·</span>
				</React.Fragment>
			))}
		</div>
	);
};

/**
 * One moving row of the »Posebna izdanja« section. On desktop each slot
 * carries the implemented SaleCard — the same ticket sold on /premade,
 * stripped to title, price and signature (no meta row, no quick-add);
 * below smallDesktop the row stays the endless roll of joined RollCard
 * tickets.
 */
const MarqueeRow = ({
	quizzes,
	reverse,
}: {
	quizzes: IPremadeQuiz[];
	reverse?: boolean;
}) => {
	const { t } = useTranslation();
	const { width } = useWindowListener();
	const trackRef = useRef<HTMLDivElement>(null);
	useMarqueeDrag(trackRef, !!reverse, quizzes.length);
	const isDesktop = width >= breakpoints.smallDesktop;

	if (quizzes.length === 0) return null;
	const doubled = [...quizzes, ...quizzes];

	return (
		<Marquee ref={trackRef} $reverse={reverse}>
			{doubled.map((quiz, i) =>
				isDesktop ? (
					<SaleSlot key={`${quiz.uuid}-${i}`}>
						<PremadeQuizCardComponent quiz={quiz} hideMeta hideQuickAdd />
					</SaleSlot>
				) : (
					<RollCard
						as={Link}
						key={`${quiz.uuid}-${i}`}
						to={`${RoutePath.PremadeQuizzes}/quiz/${quiz.uuid}`}
						onClick={scrollToTop}
					>
						<div className='ph' data-darkzone>
							<img
								src={`${import.meta.env.VITE_ASSETS_URL}/thematic_quizzes/${
									quiz.cover_photo_small
								}`}
								alt={quiz.title}
								loading='lazy'
							/>
							<span className='shade' />
							<div className='plate'>
								<h3>{quiz.title}</h3>
							</div>
						</div>
						<div className='stub'>
							<span className='pr'>
								<span className='lb'>{t('premade.card.priceLabel')}</span>
								<span className='eur'>{formatPrice(quiz.price)}</span>
							</span>
							<span className='by'>
								<CardAuthorMark displayName={quiz.author?.display_name} />
							</span>
						</div>
					</RollCard>
				)
			)}
		</Marquee>
	);
};

export const Homepage: React.FC = () => {
	const { t } = useTranslation();
	const { premadeQuizzes, getPremadeQuizzes } = usePremadeQuizzes();
	const hasLoadedPremadeRef = useRef(false);
	const heroImgRef = useRef<HTMLImageElement>(null);
	const tvImgRef = useRef<HTMLImageElement>(null);

	useParallax(heroImgRef, 0.14);
	useParallax(tvImgRef, 0.1);

	useEffect(() => {
		if (hasLoadedPremadeRef.current) return;
		hasLoadedPremadeRef.current = true;
		getPremadeQuizzes();
	}, [getPremadeQuizzes]);

	const [marqueeTop, marqueeBottom] = useMemo(() => {
		const withCovers = (premadeQuizzes ?? []).filter(
			(quiz) =>
				quiz.cover_photo_small && !quiz.title.toLowerCase().includes('eurosong')
		);
		const picked = withCovers.slice(0, 12);
		const half = Math.ceil(picked.length / 2);
		return [picked.slice(0, half), picked.slice(half)];
	}, [premadeQuizzes]);

	const subtitle = t('homepage.landing.subtitle');
	const subtitleDotAt = subtitle.indexOf('. ');
	const subtitleLead =
		subtitleDotAt === -1 ? subtitle : subtitle.slice(0, subtitleDotAt + 1);
	const subtitleHand =
		subtitleDotAt === -1 ? '' : subtitle.slice(subtitleDotAt + 2);

	return (
		<HomePage>
			<Navigation />

			<Hero>
				<h1>
					<span className='row'>
						<span>{t('homepage.landing.title1')}</span>
					</span>
					<span className='row'>
						<span>
							<em>{t('homepage.landing.title2')}</em>
						</span>
					</span>
				</h1>
				<div className='sub'>
					<p>{subtitleLead}</p>
					<div className='cta'>
						{subtitleHand && <span className='hand'>{subtitleHand}</span>}
						<BtnTeal as={Link} to={RoutePath.QuickPick}>
							{t('homepage.redesign.heroCta')}
						</BtnTeal>
					</div>
				</div>
			</Hero>
			<HeroMedia data-darkzone>
				<picture>
					<source
						media='(max-width: 720px)'
						srcSet='/promo_cover2-mobile.webp'
					/>
					<img
						ref={heroImgRef}
						src='/promo_cover2.webp'
						alt={t('homepage.landing.title1')}
					/>
				</picture>
				<span className='shade-top' />
				{subtitleHand && <span className='hand'>{subtitleHand}</span>}
				<span className='tag'>
					<IcTv size={18} /> {t('homepage.redesign.heroTag')}
				</span>
			</HeroMedia>

			<Kinetic>
				<KineticLine
					words={t('homepage.redesign.kineticLine1').split('|')}
					factor={-0.28}
					className='k1'
				/>
				<KineticLine
					words={t('homepage.redesign.kineticLine2').split('|')}
					factor={0.2}
					className='k2'
				/>
			</Kinetic>

			<Formats>
				<Reveal>
					<SecHead>
						<span className='hand'>{t('homepage.redesign.formatsHand')}</span>
					</SecHead>
				</Reveal>
				<Reveal>
					<FRow as={Link} to={RoutePath.QuickPick}>
						<div className='in'>
							<span className='num'>01</span>
							<h3>{t('homepage.nav.quick')}</h3>
							<span className='desc'>{t('homepage.quick.description')}</span>
							<span className='arr'>
								<IcArrowRight size={20} />
							</span>
						</div>
					</FRow>
				</Reveal>
				<Reveal>
					<FRow as={Link} to={RoutePath.GeneratorCustomizer}>
						<div className='in'>
							<span className='num'>02</span>
							<h3>{t('homepage.nav.generator')}</h3>
							<span className='desc'>
								{t('homepage.generator.description')}
							</span>
							<span className='arr'>
								<IcArrowRight size={20} />
							</span>
						</div>
					</FRow>
				</Reveal>
				<Reveal>
					<FRow as={Link} to={RoutePath.PremadeQuizzes} onClick={scrollToTop}>
						<div className='in'>
							<span className='num'>03</span>
							<h3>{t('homepage.nav.premade')}</h3>
							<span className='desc'>{t('homepage.premade.description')}</span>
							<span className='arr'>
								<IcArrowRight size={20} />
							</span>
						</div>
					</FRow>
				</Reveal>
			</Formats>

			<TvBlock data-darkzone>
				<picture>
					<source
						media='(max-width: 720px)'
						srcSet='/mocks/mock_tv2-mobile.webp'
					/>
					<img
						className='bg'
						ref={tvImgRef}
						src='/mocks/mock_tv2.webp'
						alt=''
					/>
				</picture>
				<div className='shade' />
				<div className='inner'>
					<Reveal as='h2'>
						{t('homepage.redesign.tvTitle1')}
						<br />
						<em>{t('homepage.redesign.tvTitle2')}</em>
					</Reveal>
					<Reveal as='span' className='hand' delay={1}>
						{t('homepage.redesign.tvHand')}
					</Reveal>
				</div>
			</TvBlock>

			<Paper>
				<Reveal>
					<SecHead style={{ position: 'relative' }}>
						<h2>
							{t('homepage.touch.stepsTitle1')}{' '}
							{t('homepage.touch.stepsTitleAccent')}
						</h2>
					</SecHead>
				</Reveal>
				<Steps>
					<Reveal className='stp'>
						<span className='n'>1</span>
						<h3>{t('homepage.touch.step1Title')}</h3>
						<span className='hand'>{t('homepage.touch.step1Body')}</span>
					</Reveal>
					<Reveal className='stp' delay={1}>
						<svg
							className='scrib'
							width='54'
							height='40'
							viewBox='0 0 54 40'
							fill='none'
						>
							<path
								d='M4 34C16 12 34 6 50 10m0 0-8-5m8 5-6 7'
								stroke='currentColor'
								strokeWidth='2.6'
								strokeLinecap='round'
								strokeLinejoin='round'
							/>
						</svg>
						<span className='n'>2</span>
						<h3>{t('homepage.touch.step2Title')}</h3>
						<span className='hand'>{t('homepage.touch.step2Body')}</span>
					</Reveal>
					<Reveal className='stp' delay={2}>
						<span className='n'>3</span>
						<h3>{t('homepage.touch.step3Title')}</h3>
						<span className='hand'>{t('homepage.touch.step3Body')}</span>
					</Reveal>
					<Reveal className='stp' delay={3}>
						<span className='n'>4</span>
						<h3>{t('homepage.touch.step4Title')}</h3>
						<span className='hand'>{t('homepage.touch.step4Body')}</span>
					</Reveal>
				</Steps>
				<Reveal>
					<PaperCta>
						<BtnInk as={Link} to={RoutePath.Profile}>
							{t('homepage.touch.finaleCta')}
						</BtnInk>
						<span className='hand'>{t('homepage.intro.description')}</span>
					</PaperCta>
				</Reveal>
			</Paper>

			{(marqueeTop.length > 0 || marqueeBottom.length > 0) && (
				<Covers>
					<Reveal>
						<SecHead>
							<h2>{t('homepage.premade.title')}</h2>
							<ArrowLink
								as={Link}
								to={RoutePath.PremadeQuizzes}
								onClick={scrollToTop}
							>
								{t('homepage.premade.cta')} <IcArrowRight size={18} />
							</ArrowLink>
						</SecHead>
					</Reveal>
					<MarqueeRow quizzes={marqueeTop} />
					<MarqueeRow quizzes={marqueeBottom} reverse />
				</Covers>
			)}

			<GiftSection>
				<Reveal>
					<GiftPromo as={Link} to={RoutePath.Voucher} onClick={scrollToTop}>
						<div className='in'>
							<span className='thumb'>
								<span className='stack'>
									<HeroVoucher />
									<HeroVoucher />
									<HeroVoucher />
								</span>
							</span>
							<div>
								<h3>
									{t('homepage.redesign.giftTitle1')}{' '}
									{t('homepage.redesign.giftTitle2')}
								</h3>
								<span className='hand'>{t('homepage.redesign.giftHand')}</span>
							</div>
							<span className='desc'>{t('voucherShop.hero.text')}</span>
							<span className='arr'>
								<IcArrowRight size={20} />
							</span>
						</div>
					</GiftPromo>
				</Reveal>
			</GiftSection>

			<Finale>
				<Reveal as='h2'>
					{t('homepage.touch.finaleTitle')}
					<br />
					<em>{t('homepage.touch.finaleAccent')}</em>
				</Reveal>
				<Reveal as='span' className='hand' delay={1}>
					{t('homepage.redesign.finaleHand')}
				</Reveal>
				<Reveal delay={2}>
					<BtnTeal as={Link} to={RoutePath.Profile}>
						{t('homepage.touch.finaleCta')}
					</BtnTeal>
				</Reveal>
			</Finale>
		</HomePage>
	);
};
